import { Grid, useTheme } from "@mui/material";
import React, { useState } from "react";
import CommonFormField from "../../common/components/CommonFormField";
import CommonSelect from "../../common/components/CommonSelect";
import CommonCard from "../../common/components/CommonCard";
import { Academics, ActivitiesStatus, Durations, ProficiencyLevels } from "../../common/const";
import CommonButton from "../../common/components/CommonButton";
import { Add } from "@mui/icons-material";
import { setLoading } from "../../features/authSlice";
import { useDispatch, useSelector } from "react-redux";
import LoadingSpinner from "../../common/components/LoadingSpinner";
import { createCodingSkill } from "../../api/codingSKillApi";
import { showNotification } from "../../common/components/Notification/ToastifyNotification";

const SkillsInfo = ({
    formData,
    setFormData
}) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const { loading, user } = useSelector((state) => state.auth);
    const [errors, setErrors] = useState({});
    const validateField = (name, value, file) => {
        switch (name) {
            case 'courseName':
                if (!value) return 'Course/Class Name is required';
                return '';
            case 'proficiencyLevel':
                if (!value) return 'Proficiency Level is required';
                return '';
            case 'status':
                if (!value) return 'Status is required';
                return '';
            case 'certificate': // Assuming this is your file input name
                if (file) {
                    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
                    if (file.size > maxSize) {
                        return 'File size must be less than 5MB';
                    }
                }
                return '';
            default:
                return '';
        }
    };

    const handleInputChange = (e) => {
        const { name, value, files } = e.target;
        if (files && files.length > 0) {
            const file = files;

            // Validate file size immediately
            const error = validateField(name, '', file); // Pass empty string for value, file for validation

            setFormData((prev) => ({
                ...prev,
                coding: {
                    ...prev.coding,
                    [name]: file,
                },
            }));

            // Set or clear error
            if (error) {
                setErrors(prev => ({ ...prev, [name]: error }));
            } else {
                setErrors(prev => ({ ...prev, [name]: undefined }));
            }
        } else {
            setFormData((prev) => ({
                ...prev,
                coding: {
                    ...prev.coding,
                    [name]: value,
                },
            }));
        }

        // Clear error for the field being changed
        if (errors[name]) {
            const error = validateField(name, value);
            if (!error) {
                setErrors(prev => ({ ...prev, [name]: undefined }));
            } else {
                setErrors(prev => ({ ...prev, [name]: error }));
            }
        }
    };

    const validateForm = () => {
        const newErrors = {};
        Object.keys(formData.coding).forEach(key => {
            const error = validateField(key, formData.coding[key]);
            if (error) newErrors[key] = error;
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        try {
            if (!validateForm()) return;
            dispatch(setLoading({
                loading: true
            }));
            const formDataToSend = new FormData();

            // Add each property to FormData
            formDataToSend.append('course_name', formData.coding.courseName);
            formDataToSend.append('proficiency_level', formData.coding.proficiencyLevel);
            formDataToSend.append('exam_taken_date', formData.coding.examTakenDate !== '' ? new Date(formData.coding.examTakenDate).toISOString() : null);
            formDataToSend.append('skill_status', formData.coding.codingStatus);
            formDataToSend.append('status', formData.coding.status);
            formDataToSend.append('duration', formData.coding.duration || 0);
            formDataToSend.append('start_date', formData.coding.startdate !== '' ? new Date(formData.coding.startdate).toISOString() : null);
            formDataToSend.append('end_date', formData.coding.enddate !== '' ? new Date(formData.coding.enddate).toISOString() : null);
            formDataToSend.append('student_id', user.student_id);
            formDataToSend.append('student_grade', localStorage.getItem('current_grade'));
            if (formData.coding.certificate && formData.coding.certificate.length > 0) {
                const files = Array.from(formData.coding.certificate);
                files.forEach((file, index) => {
                    formDataToSend.append('documents', file);
                });
            }
            const response = await createCodingSkill(formDataToSend);
            if (response.data.skill_id) {
                showNotification('Coding/Skill added successfully', 'success');
                dispatch(setLoading({
                    loading: false
                }));
                setFormData((prev) => ({
                    ...prev,
                    coding: {
                        status: Academics[0].value,
                        courseName: '',
                        proficiencyLevel: '',
                        examTakenDate: '',
                        codingStatus: '',
                        duration: '',
                        startdate: '',
                        enddate: '',
                        certificate: ''
                    },
                    skillCliked: true
                }));
            }
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    };

    const getMinDate = () => {
        const date = formData.coding.startdate ? new Date(formData.coding.startdate) : new Date();
        date.setDate(date.getDate() + 1); // Add 7 days
        return date.toISOString().split('T')[0];
    };

    return (
        <Grid container spacing={1.5} mt={0.5} mb={2}>
            {loading && (
                <LoadingSpinner />
            )}
            <Grid item xs={12}>
                <CommonCard
                    marginTop={0.5}
                    borderRadius={1}
                    border={`1px solid ${theme.palette.background.light}`}
                    content={
                        <Grid container spacing={1.5}>
                            <Grid item xs={12} md={12}>
                                <CommonSelect
                                    label="Status"
                                    name="status"
                                    value={formData.coding.status}
                                    onChange={handleInputChange}
                                    placeholder="Select Status"
                                    error={errors.status}
                                    options={Academics}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <CommonFormField
                                    label="Course/Class Name"
                                    type="email"
                                    name="courseName"
                                    value={formData.coding.courseName}
                                    onChange={handleInputChange}
                                    error={errors.courseName}
                                    placeholder="e.g., Python, Java, C++"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <CommonSelect
                                    label="Proficiency Level"
                                    name="proficiencyLevel"
                                    value={formData.coding.proficiencyLevel}
                                    onChange={handleInputChange}
                                    placeholder="Select Level"
                                    error={errors.proficiencyLevel}
                                    options={ProficiencyLevels}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <CommonFormField
                                    label="Exam Taken Date"
                                    type="date"
                                    name="examTakenDate"
                                    value={formData.coding.examTakenDate}
                                    onChange={handleInputChange}
                                    placeholder="Select exam date"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <CommonSelect
                                    label="Select Status"
                                    name="codingStatus"
                                    value={formData.coding.codingStatus}
                                    onChange={handleInputChange}
                                    placeholder="Select Status"
                                    options={ActivitiesStatus}
                                />
                            </Grid>
                            <Grid item xs={12} md={12}>
                                <CommonSelect
                                    label="Select Duration"
                                    name="duration"
                                    value={formData.coding.duration}
                                    onChange={handleInputChange}
                                    placeholder="Select month"
                                    options={Durations}
                                />
                            </Grid>
                            <Grid item xs={12} md={formData.coding.status === 'PAST' ? 6 : 12}>
                                <CommonFormField
                                    label="Start Date"
                                    type="date"
                                    name="startdate"
                                    value={formData.coding.startdate}
                                    onChange={handleInputChange}
                                    error={errors.startdate}
                                    placeholder="Select Start Date"
                                />
                            </Grid>
                            {formData.coding.status === 'PAST' && (
                                <Grid item xs={12} md={6}>
                                    <CommonFormField
                                        label="End Date"
                                        type="date"
                                        name="enddate"
                                        value={formData.coding.enddate}
                                        onChange={handleInputChange}
                                        placeholder="Select End Date"
                                        minDate={getMinDate()}
                                    />
                                </Grid>
                            )}
                            <Grid item xs={12} md={12}>
                                <CommonFormField
                                    label="Upload Certificate"
                                    type="file"
                                    name="certificate"
                                    value={formData.coding.certificate?.[0]?.name}
                                    onChange={handleInputChange}
                                    placeholder="PDF"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <CommonButton
                                    label="Add Skill"
                                    background={theme.palette.secondary.main}
                                    color={theme.palette.background.main}
                                    fontWeight={600}
                                    handleClick={() => handleSubmit()}
                                    startIcon={<Add />}
                                    marginBottom={1}
                                />
                            </Grid>
                        </Grid>
                    }
                    paddingBottom={0}
                    padding="16px 16px 0px 16px"
                />
            </Grid>
        </Grid>
    )
}

export default SkillsInfo;